import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Download, Mail, Search, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString('en-ZA', { maximumFractionDigits: 2 }));

const WeekSummary = ({ s }) => (
  <Card data-testid={`week-summary-${s.key}`}>
    <CardContent className="pt-4 space-y-1">
      <p className="text-xs text-muted-foreground">Week {s.key} · {s.start.slice(5)} – {s.end.slice(5)}</p>
      <p className="text-2xl font-bold">{s.suppliers_counted.length}<span className="text-sm font-normal text-muted-foreground"> suppliers counted</span></p>
      <p className="text-xs text-muted-foreground">{s.items_counted} items counted</p>
      {s.suppliers_missing.length > 0 && (
        <p className="text-xs text-amber-700" title={s.suppliers_missing.join(', ')}>Not counted: {s.suppliers_missing.slice(0, 3).join(', ')}{s.suppliers_missing.length > 3 ? ` +${s.suppliers_missing.length - 3}` : ''}</p>
      )}
    </CardContent>
  </Card>
);

export const WeeklyComparison = () => {
  const [branches, setBranches] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [branchId, setBranchId] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [weeks, setWeeks] = useState('4');
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    Promise.all([axios.get(`${API}/branches`, { withCredentials: true }), axios.get(`${API}/suppliers`, { withCredentials: true })])
      .then(([b, s]) => { setBranches(b.data); setSuppliers(s.data.filter((x) => !x.archived)); });
  }, []);

  const params = useCallback(() => {
    const p = { weeks };
    if (branchId) p.branch_id = branchId;
    if (supplierId) p.supplier_id = supplierId;
    return p;
  }, [branchId, supplierId, weeks]);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/reports/weekly-comparison`, { withCredentials: true, params: params() })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || 'Failed to load report'))
      .finally(() => setLoading(false));
  }, [params]);

  const doExport = () => window.open(`${API}/reports/weekly-comparison/export?${new URLSearchParams(params())}`, '_blank');

  const emailMe = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API}/reports/weekly-comparison/email-me`, {}, { withCredentials: true });
      toast.success(r.data.message);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send email'); }
    finally { setSending(false); }
  };

  const rows = (data?.rows || []).filter((r) => (!flaggedOnly || r.flagged)
    && (!search || r.name.toLowerCase().includes(search.toLowerCase())));

  return (
    <div className="space-y-4" data-testid="weekly-comparison">
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Branch</label>
          <Select value={branchId || 'all'} onValueChange={(v) => setBranchId(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-52" data-testid="wc-branch-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All branches</SelectItem>
              {branches.map((b) => <SelectItem key={b.branch_id} value={b.branch_id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Supplier</label>
          <Select value={supplierId || 'all'} onValueChange={(v) => setSupplierId(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-52" data-testid="wc-supplier-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All suppliers</SelectItem>
              {suppliers.map((s) => <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Weeks</label>
          <Select value={weeks} onValueChange={setWeeks}>
            <SelectTrigger className="w-28" data-testid="wc-weeks-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {['2', '4', '6', '8', '12'].map((w) => <SelectItem key={w} value={w}>{w} weeks</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="relative">
          <label className="text-xs text-muted-foreground mb-1 block">Search</label>
          <Search className="absolute left-2.5 bottom-2.5 w-4 h-4 text-muted-foreground" />
          <Input className="pl-8 w-48" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Item name" data-testid="wc-search" />
        </div>
        <Button variant={flaggedOnly ? 'default' : 'outline'} size="sm" onClick={() => setFlaggedOnly((f) => !f)} data-testid="wc-flagged-toggle">
          <AlertTriangle className="w-4 h-4 mr-1.5" />Big changes only
        </Button>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" onClick={emailMe} disabled={sending} data-testid="wc-email-me-btn">
            <Mail className="w-4 h-4 mr-1.5" />{sending ? 'Sending...' : 'Email me this week'}
          </Button>
          <Button variant="outline" size="sm" onClick={doExport} data-testid="wc-export-btn"><Download className="w-4 h-4 mr-1.5" />Export Excel</Button>
        </div>
      </div>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {data.summary.map((s) => <WeekSummary key={s.key} s={s} />)}
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        Stock counted (in count units) per week{data ? ` · ${data.branch_name}` : ''}. Changes of {data?.flag_pct || 30}% or more versus the previous counted week are highlighted. A summary is emailed to managers and admins every Monday at 7am.
      </p>

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>
      ) : rows.length === 0 ? (
        <p className="text-center text-muted-foreground py-12" data-testid="wc-empty">No stock counts in this period.</p>
      ) : (
        <div className="border rounded-lg overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Item</TableHead>
                <TableHead>Supplier</TableHead>
                <TableHead className="text-right">PAR</TableHead>
                {data.weeks.map((w) => <TableHead key={w} className="text-right">Wk {w}</TableHead>)}
                <TableHead className="text-right">Change</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.stock_item_id} className={r.flagged ? 'bg-amber-50' : ''} data-testid={`wc-row-${r.stock_item_id}`}>
                  <TableCell>
                    <p className="font-medium">{r.name}</p>
                    <p className="text-xs text-muted-foreground">{r.unit}</p>
                  </TableCell>
                  <TableCell className="text-sm">{r.supplier_name}</TableCell>
                  <TableCell className="text-right">{fmt(r.par)}</TableCell>
                  {data.weeks.map((w) => <TableCell key={w} className="text-right tabular-nums">{fmt(r.weeks[w])}</TableCell>)}
                  <TableCell className="text-right">
                    {r.change === null ? '—' : (
                      <Badge variant={r.flagged ? 'destructive' : 'secondary'} data-testid={`wc-change-${r.stock_item_id}`}>
                        {r.change > 0 ? '+' : ''}{fmt(r.change)}{r.change_pct !== null ? ` (${r.change_pct > 0 ? '+' : ''}${r.change_pct}%)` : ''}
                      </Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};
